import React, { useState, useEffect, useCallback } from 'react';
import { View } from 'react-native';

import {
  getLoyaltyAir,
  getLoyaltyCar,
  getLoyaltyHotel,
  getLoyaltyRail,
} from '../../services/api/profile.service';

import GroupList from '../GroupList';

import { searchTypesEnum } from '../../constants/Enums';
import { CommonStyles } from '../../styles';
import Images from '../../constants/Images';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../../i18n';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Modals } from '../../constants/ScreenNames';

const getAirIcon = (code) => {
  return code ? Images.air['36x36'][code] : null;
};

const getHotelIcon = (code) => {
  return code ? Images.hotel['24x24'][code] : null;
};

const getCarIcon = (code) => {
  return code ? Images.car['50x20'][code] : null;
};

const getRailIcon = (code) => {
  return code ? Images.rail['24x24'][code] : null;
};

const LoyaltyViewer = ({ isUpdateAllowed = true }) => {
  const navigation = useNavigation();
  const { tKey } = useComponentTKey('LoyaltyViewer');
  const { t } = useTranslation(i18nNS.me);
  const [loyaltyType, setLoyaltyType] = useState(null);
  const [loyaltyCarList, setLoyaltyCarList] = useState([]);
  const [loyaltyAirList, setLoyaltyAirList] = useState([]);
  const [loyaltyHotelList, setLoyaltyHotelList] = useState([]);
  const [loyaltyRailList, setLoyaltyRailList] = useState([]);
  const [loadingHotel, setLoadingHotel] = useState(false);
  const [loadingCar, setLoadingCar] = useState(false);
  const [loadingAir, setLoadingAir] = useState(false);
  const [loadingRail, setLoadingRail] = useState(false);

  const getAirList = async () => {
    setLoadingAir(true);
    const data = await getLoyaltyAir();

    if (data.AirVendorPreferences) {
      setLoyaltyAirList(
        data.AirVendorPreferences.map((i) => {
          return {
            label: i.PreferrredAirline.Name,
            small: i.ProgramNumber,
            code: i.PreferrredAirline.Code,
            icon: getAirIcon(i.PreferrredAirline.Code),
            id: i.Id,
          };
        })
      );
    }
    setLoadingAir(false);
  };

  const getHotelList = async () => {
    setLoadingHotel(true);
    const data = await getLoyaltyHotel();
    if (data.HotelVendorPreferences) {
      setLoyaltyHotelList(
        data.HotelVendorPreferences.map((i) => ({
          label: i.HotelChainName.trim()
            .split(/[\s,\t,\n]+/)
            .join(' '),
          small: i.ProgramNumber,
          code: i.HotelChainCode,
          icon: getHotelIcon(i.HotelChainCode),
          id: i.Id,
        }))
      );
    }
    setLoadingHotel(false);
  };

  const getCarList = async () => {
    setLoadingCar(true);
    const data = await getLoyaltyCar();

    if (data.CarVendorPreferences) {
      setLoyaltyCarList(
        data.CarVendorPreferences.map((i) => ({
          label: i.CarVendorName,
          small: i.ProgramNumber,
          code: i.CarVendorCode,
          icon: getCarIcon(i.CarVendorCode),
          id: i.Id,
        }))
      );
    }
    setLoadingCar(false);
  };

  const getRailList = async () => {
    setLoadingRail(true);
    const data = await getLoyaltyRail();

    if (data.RailVendorPreferences) {
      setLoyaltyRailList(
        data.RailVendorPreferences.map((i) => ({
          label: i.PreferrredRailVendor.Name,
          small: i.ProgramNumber,
          code: i.PreferrredRailVendor.Code,
          icon: getRailIcon(i.PreferrredRailVendor.Code),
          id: i.Id,
        }))
      );
    }
    setLoadingRail(false);
  };

  useEffect(() => {
    getAirList();
    getHotelList();
    getCarList();
    getRailList();
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (loyaltyType) {
        onSubmit(loyaltyType);
      }
    }, [loyaltyType, onSubmit])
  );

  const onEditPress = (index, type) => {
    let item = {};
    switch (type) {
      case searchTypesEnum.AIR:
        item = loyaltyAirList[index];
        break;
      case searchTypesEnum.HOTEL:
        item = loyaltyHotelList[index];
        break;
      case searchTypesEnum.CAR:
        item = loyaltyCarList[index];
        break;
      case searchTypesEnum.RAIL:
        item = loyaltyRailList[index];
        break;
    }
    setLoyaltyType(type);

    navigation.navigate(Modals.LoyaltyModal, {
      type: type,
      loyaltyItem: {
        id: item.id,
        company: item.code,
        number: item.small,
      },
    });
  };

  const onAddPress = (type) => {
    setLoyaltyType(type);
    navigation.navigate(Modals.LoyaltyModal, {
      type,
      loyaltyItem: null,
      loyaltyAirList: loyaltyAirList,
    });
  };

  const onSubmit = useCallback((type) => {
    switch (type) {
      case searchTypesEnum.AIR:
        getAirList();
        break;
      case searchTypesEnum.HOTEL:
        getHotelList();
        break;
      case searchTypesEnum.CAR:
        getCarList();
        break;
      case searchTypesEnum.RAIL:
        getRailList();
        break;
    }
  }, []);

  return (
    <View>
      <GroupList
        title={t(tKey('AirlineLoyalty'))}
        items={loyaltyAirList}
        type={searchTypesEnum.AIR}
        onAddPress={onAddPress}
        onEditPress={onEditPress}
        loading={loadingAir}
        style={CommonStyles.my3}
        isUpdateAllowed={isUpdateAllowed}
      />
      <GroupList
        title={t(tKey('HotelLoyalty'))}
        type={searchTypesEnum.HOTEL}
        items={loyaltyHotelList}
        onAddPress={onAddPress}
        onEditPress={onEditPress}
        loading={loadingHotel}
        style={CommonStyles.my3}
        isUpdateAllowed={isUpdateAllowed}
      />
      <GroupList
        title={t(tKey('CarLoyalty'))}
        type={searchTypesEnum.CAR}
        items={loyaltyCarList}
        onAddPress={onAddPress}
        onEditPress={onEditPress}
        loading={loadingCar}
        style={CommonStyles.my3}
        isUpdateAllowed={isUpdateAllowed}
      />
      <GroupList
        title={t(tKey('RailLoyalty'))}
        type={searchTypesEnum.RAIL}
        items={loyaltyRailList}
        onAddPress={onAddPress}
        onEditPress={onEditPress}
        loading={loadingRail}
        style={CommonStyles.my3}
        isUpdateAllowed={isUpdateAllowed}
      />
    </View>
  );
};

export default LoyaltyViewer;
